import React from "react";
import {
  getUniqueMovies,
  getDbcVerifiedSubjectsFirst,
  getLocalAgencySubjects,
  materialContainsDanish
} from "../../apps/material/helper";
import {
  constructAdvancedSearchSubjectUrl,
  constructDK5SearchUrl,
  constructMaterialUrl,
  constructSearchUrl,
  constructSubjectSearchUrl
} from "../../core/utils/helpers/url";
import { useConfig } from "../../core/utils/config";
import { useText } from "../../core/utils/text";
import { Work } from "../../core/utils/types/entities";
import { Pid, WorkId } from "../../core/utils/types/ids";
import { useUrls } from "../../core/utils/url";
import HorizontalTermLine from "../horizontal-term-line/HorizontalTermLine";
import {
  materialIsFiction,
  materialFictionNonfictionIsNotSpecified
} from "../../core/utils/helpers/general";
import SeriesList from "../card-item-list/card-list-item/series-list";
import MaterialContents from "./MaterialContents/MaterialContents";
import ButtonShare from "../button-share/button-share";

export interface MaterialDescriptionProps {
  pid: Pid;
  work: Work;
}

const MaterialDescription: React.FC<MaterialDescriptionProps> = ({ work }) => {
  const t = useText();
  const u = useUrls();
  const config = useConfig();
  const searchUrl = u("searchUrl");
  const advancedSearchUrl = u("advancedSearchUrl");
  const materialUrl = u("materialUrl");
  const {
    fictionNonfiction,
    series,
    subjects,
    relations,
    dk5MainEntry,
    manifestations
  } = work;

  const { enabled: showShareButtons } = config<{ enabled: boolean }>(
    "shareConfig",
    {
      transformer: "jsonParse"
    }
  );

  const localSubjectsAgencyIds = config("localSubjectsAgencyIdsConfig", {
    transformer: "stringToArray"
  });

  const isFiction = materialIsFiction(work);

  // Show DK5 for all non-fiction works OR fiction works in non-Danish languages
  const shouldShowDk5 =
    !isFiction || (isFiction && !materialContainsDanish(work));

  const seriesMembersList =
    (series &&
      series[0]?.members.map((member) => {
        // TODO: Since the series has changed it structure and can have multiple members
        // we need to double check if we can only look at the first member entry.
        return {
          url: constructMaterialUrl(materialUrl, member.work.workId as WorkId),
          term: member.work.titles.main[0]
        };
      })) ??
    [];

  const dbcSubjects = getDbcVerifiedSubjectsFirst(subjects).map((item) => ({
    url: constructSubjectSearchUrl(searchUrl, item),
    term: item
  }));

  const localSubjects = getLocalAgencySubjects(
    manifestations.all,
    localSubjectsAgencyIds
  )
    .filter((item) => !dbcSubjects.some((dbc) => dbc.term === item))
    .map((item) => ({
      url: constructAdvancedSearchSubjectUrl(advancedSearchUrl, item),
      term: item
    }));

  const subjectsList = [...localSubjects, ...dbcSubjects];

  const filmAdaptationsList = getUniqueMovies(relations).map((item) => {
    return {
      url: constructMaterialUrl(materialUrl, item.ownerWork.workId as WorkId),
      term: item.ownerWork.titles.main[0]
    };
  });

  const fictionNonfictionList =
    fictionNonfiction && !materialFictionNonfictionIsNotSpecified(work)
      ? [
          {
            url: constructSearchUrl(searchUrl, fictionNonfiction.display),
            term: fictionNonfiction.display
          }
        ]
      : [];

  const bestRepresentationContents =
    work.manifestations.bestRepresentation?.contents;

  return (
    <section className="material-description" data-cy="material-description">
      <>
        {!!work.abstract?.length && (
          <>
            <h2 className="material-description__heading">
              {t("descriptionHeadlineText")}
            </h2>
            {work.abstract.map((line, index) => (
              <p key={index} className="material-description__content">
                {line}
              </p>
            ))}
          </>
        )}
        {bestRepresentationContents && (
          <MaterialContents contents={bestRepresentationContents} />
        )}
        <div className="material-description__links">
          {shouldShowDk5 && dk5MainEntry && (
            <HorizontalTermLine
              title={t("subjectNumberText")}
              linkList={[
                {
                  url: constructDK5SearchUrl(searchUrl, dk5MainEntry.code),
                  term: dk5MainEntry.display
                }
              ]}
            />
          )}
          <SeriesList
            series={series}
            searchUrl={searchUrl}
            t={t}
            workId={work.workId}
            dataCy="material-description-series"
          />
          <HorizontalTermLine
            title={t("inSameSeriesText")}
            linkList={seriesMembersList}
            dataCy="material-description-series-members"
          />
          <HorizontalTermLine
            title={t("identifierText")}
            linkList={subjectsList}
            dataCy="material-description-identifier"
          />
          <HorizontalTermLine
            title={t("fictionNonfictionText")}
            linkList={fictionNonfictionList}
            dataCy="material-description-fiction-nonfiction"
          />
          <HorizontalTermLine
            title={t("filmAdaptationsText")}
            linkList={filmAdaptationsList}
            dataCy="material-description-film-adaptations"
          />
        </div>
        {showShareButtons && <ButtonShare className="mt-64" />}
      </>
    </section>
  );
};

export default MaterialDescription;
